import { isLiabilityAccountType } from '@fruitful/fruitils';
import {
    type AccountSummary,
    AnalyticsUserEvent,
    LinkState,
    Provider,
} from '@fruitful/types';
import { router, useLocalSearchParams } from 'expo-router';
import { useAtomValue } from 'jotai';
import { groupBy } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import {
    BackButton,
    BackButtonIconType,
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FullScreenContainer,
    H5Regular,
    Icon,
    isNonMobileWeb,
    PlusIcon,
    ScrollView,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { getUserGuideName } from '../../common/utils/userUtils';
import { useViewEvents } from '../../events/hooks/useViewEvents';
import InstitutionAccounts from '../../holistic/components/InstitutionAccounts';
import useConnectQuilttInstitution from '../../holistic/hooks/useConnectQuilttInstitution';
import useGetHolisticSummary from '../../holistic/hooks/useGetHolisticSummary';
import { holisticAccountsAtom } from '../../holistic/state/HolisticAtoms';
import type { QuilttConnectorSource } from '../../holistic/types/Quiltt';
import { track } from '../../logger/Logger';

const ConnectAccounts = () => {
    const { trackViewOnce } = useViewEvents();
    trackViewOnce('Onboarding/ConnectAccounts');
    const context: QuilttConnectorSource = 'onboarding';

    const { openConnector } = useLocalSearchParams<{
        openConnector?: string;
    }>();
    // for some reason, we need this extra piece of state to track if we launched the connector when we landed
    // here with the openConnector query param. otherwise, what will happen is once the quiltt connector closes for the first
    // time after a creating a connection, it will briefly re-open and then close itself.
    const [hasOpenedConnectorOnLaunch, setHasOpenedConnectorOnLaunch] =
        useState(false);

    const [isLoading, setIsLoading] = useState(true);

    const holisticAccounts = useAtomValue(holisticAccountsAtom);

    const { isLoading: isLoadingHolisticData, refetch } =
        useGetHolisticSummary(true);

    const user = useAuthedUser();

    const { connectInstitution } = useConnectQuilttInstitution(context);

    const inOnboardingFlow = context === 'onboarding';

    // for some godforsaken reason, react query is doing some weird caching which sometimes results in an incomplete summary
    // being returned from the hook. this forces a refetch after 1 second to actually refresh the data
    useEffect(() => {
        setTimeout(async () => {
            await refetch();
            setIsLoading(false);
        }, 1000);
    }, []);

    const accountsNeedDetails = useMemo(() => {
        if (inOnboardingFlow) return false;

        const needingDetails = holisticAccounts.filter(
            (account: AccountSummary) => {
                if (
                    [LinkState.DELETED, LinkState.ERROR].includes(
                        account.linkState as LinkState,
                    )
                ) {
                    return false;
                }

                if (account.provider === Provider.SPINWHEEL) return false;

                if (!isLiabilityAccountType(account.type)) return false;

                const isCredit = account.type?.toLowerCase() === 'credit';

                if (isCredit) {
                    return account.entireBalancePaidMonthly == null;
                }

                // For other liability accounts, needed if missing minPayment or interestRate
                return (
                    account.minimumPayment == null ||
                    account.interestRate == null
                );
            },
        );
        return needingDetails.length > 0;
    }, [holisticAccounts]);

    const enableContinue = useMemo(() => {
        if (isLoadingHolisticData) return false;
        return holisticAccounts.length > 0 && !accountsNeedDetails;
    }, [accountsNeedDetails, holisticAccounts, isLoadingHolisticData]);

    // The Connect Accounts page only handles Active (or in process of Connecting) Quiltt
    const accountsToDisplay = holisticAccounts.filter((account) => {
        const isActiveQuilttAccount =
            account.provider === Provider.QUILTT &&
            (account.linkState === LinkState.CONNECTED ||
                account.linkState === LinkState.CONNECTING);
        const isManual = account.provider === Provider.MANUAL;

        return isActiveQuilttAccount || isManual;
    });
    // Group the accounts by institution.
    const accountsGroupedByInstitution = groupBy(
        accountsToDisplay,
        'institutionName',
    );

    const continueText = inOnboardingFlow ? "That's all of them" : 'Continue';

    const skipSpinwheel =
        (user.spinwheelId != null && user.spinwheelId !== '') ||
        user.spinwheelOverride === true;

    const continueRoute = skipSpinwheel
        ? '/onboarding/expenses/bills-processing'
        : '/onboarding/expenses/disclaimer';
    useEffect(() => {
        if (openConnector === 'true' && !hasOpenedConnectorOnLaunch) {
            // set this to true so it doesn't reopen when coming back here from the quiltt connector
            setHasOpenedConnectorOnLaunch(true);
            connectInstitution();
        }
    }, [connectInstitution, hasOpenedConnectorOnLaunch]);

    return (
        <FullScreenContainer
            scrollDisabled={true}
            header={
                <XStack
                    padding={8}
                    flex={1}
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <BackButton
                        iconType={BackButtonIconType.ARROW_LEFT}
                        {...(accountsToDisplay?.length > 0 && {
                            customBackFn: () => {
                                router.navigate('/');
                            },
                        })}
                    />
                </XStack>
            }
        >
            <ScrollView
                alwaysBounceVertical={false}
                marginTop={16}
                showsVerticalScrollIndicator={isNonMobileWeb}
            >
                <YStack flex={1}>
                    {holisticAccounts.length > 0 && (
                        <YStack gap={8}>
                            {isLoadingHolisticData ||
                            holisticAccounts.length === 0 ||
                            isLoading ? (
                                <Spinner />
                            ) : (
                                <YStack gap={24}>
                                    <YStack gap={8}>
                                        <H5Regular>
                                            Connected accounts
                                        </H5Regular>
                                        <BodyMediumRegular color="$gray1100">
                                            {inOnboardingFlow
                                                ? 'Checking accounts you pay bills from, and credit cards you’re spending on day to day.'
                                                : 'Review the connected accounts below and add any additional or missing details.'}
                                        </BodyMediumRegular>
                                    </YStack>
                                    {Object.entries(
                                        accountsGroupedByInstitution,
                                    ).map(
                                        (
                                            [institutionName, accounts],
                                            index,
                                        ) => {
                                            // Use the first account in a set of accounts for an institution to get institution details.
                                            const { logo, provider } =
                                                accounts[0] ?? {};

                                            return (
                                                <InstitutionAccounts
                                                    key={index}
                                                    institutionName={
                                                        institutionName
                                                    }
                                                    institutionLogo={logo ?? ''}
                                                    connectionId={
                                                        accounts[0]
                                                            ?.connectionId ?? ''
                                                    }
                                                    accounts={accounts}
                                                    isManual={
                                                        provider ===
                                                        Provider.MANUAL
                                                    }
                                                    context={context}
                                                    shouldRenderAddAccountButton={
                                                        false
                                                    }
                                                />
                                            );
                                        },
                                    )}
                                    <YStack
                                        borderWidth={1}
                                        borderRadius={16}
                                        borderColor="$green600"
                                        overflow="hidden"
                                    >
                                        <YStack
                                            backgroundColor="$green300"
                                            paddingHorizontal={16}
                                            paddingVertical={12}
                                            alignItems="center"
                                            justifyContent="space-between"
                                            gap={16}
                                        >
                                            <BodySmallRegular color="$gray1200">
                                                Connect all of your spending
                                                accounts - accurately capture
                                                your expenses to make your first
                                                call with{' '}
                                                {getUserGuideName(user)}{' '}
                                                focused, helpful, and worth your
                                                time.
                                            </BodySmallRegular>
                                            <XStack
                                                borderRadius={16}
                                                borderWidth={1}
                                                borderColor="$green1100"
                                                padding={16}
                                                gap={8}
                                                width="100%"
                                                justifyContent="center"
                                                alignItems="center"
                                                cursor="pointer"
                                                onPress={() => {
                                                    connectInstitution();
                                                }}
                                            >
                                                <Icon
                                                    icon={PlusIcon}
                                                    size={16}
                                                    color="$green1100"
                                                />
                                                <BodyMediumEmphasized color="$green1100">
                                                    Add more spending accounts
                                                </BodyMediumEmphasized>
                                            </XStack>
                                        </YStack>
                                    </YStack>
                                    <Button
                                        variant={ButtonVariant.PRIMARY}
                                        disabled={!enableContinue}
                                        size={ButtonSize.LARGE}
                                        text={continueText}
                                        padding={16}
                                        onPress={() => {
                                            track(
                                                AnalyticsUserEvent.ONBOARDING_FUNNEL_ACCOUNTS_CONNECTED,
                                                undefined,
                                                true,
                                            );
                                            router.navigate(continueRoute);
                                        }}
                                    />
                                </YStack>
                            )}
                        </YStack>
                    )}
                </YStack>
            </ScrollView>
        </FullScreenContainer>
    );
};

export default ConnectAccounts;
